import { useMemo, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CtaBand from "@/components/home/CtaBand";
import { fmtAUD } from "@/data/content";

function RepaymentCalc() {
  const [amount, setAmount] = useState(750000);
  const [rate, setRate] = useState(6.19);
  const [years, setYears] = useState(30);

  const { monthly, totalInterest, points } = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    const m = r > 0 ? (amount * r) / (1 - Math.pow(1 + r, -n)) : amount / n;
    const pts = [];
    for (let y = 0; y <= years; y++) {
      const k = y * 12;
      const bal = r > 0
        ? Math.max(0, amount * Math.pow(1 + r, k) - (m * (Math.pow(1 + r, k) - 1)) / r)
        : Math.max(0, amount - m * k);
      pts.push({ year: `Yr ${y}`, balance: Math.round(bal) });
    }
    return { monthly: m, totalInterest: Math.max(0, m * n - amount), points: pts };
  }, [amount, rate, years]);

  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
      <div className="space-y-10">
        <div>
          <div className="flex justify-between items-baseline mb-4">
            <Label htmlFor="loan-amount" className="text-sm font-semibold text-ink">Loan amount</Label>
            <span className="font-display text-xl font-bold text-ink" data-testid="calculator-amount-display">{fmtAUD(amount)}</span>
          </div>
          <Slider data-testid="calculator-loan-amount-input" value={[amount]} onValueChange={([v]) => setAmount(v)} min={100000} max={3000000} step={10000} />
          <div className="flex justify-between text-[11px] text-ink/40 mt-2"><span>$100k</span><span>$3M</span></div>
        </div>
        <div>
          <div className="flex justify-between items-baseline mb-4">
            <Label htmlFor="interest-rate" className="text-sm font-semibold text-ink">Interest rate</Label>
            <span className="font-display text-xl font-bold text-ink" data-testid="calculator-rate-display">{rate.toFixed(2)}% p.a.</span>
          </div>
          <Slider data-testid="calculator-rate-input" value={[rate]} onValueChange={([v]) => setRate(v)} min={3} max={10} step={0.05} />
          <div className="flex justify-between text-[11px] text-ink/40 mt-2"><span>3%</span><span>10%</span></div>
        </div>
        <div>
          <div className="flex justify-between items-baseline mb-4">
            <Label htmlFor="loan-term" className="text-sm font-semibold text-ink">Loan term</Label>
            <span className="font-display text-xl font-bold text-ink" data-testid="calculator-term-display">{years} years</span>
          </div>
          <Slider data-testid="calculator-term-input" value={[years]} onValueChange={([v]) => setYears(v)} min={5} max={30} step={1} />
          <div className="flex justify-between text-[11px] text-ink/40 mt-2"><span>5 yrs</span><span>30 yrs</span></div>
        </div>
      </div>

      <div>
        <div className="rounded-3xl bg-obsidian text-white p-8 sm:p-10 grain relative overflow-hidden" data-testid="repayment-result">
          <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
          <div className="text-xs uppercase tracking-[0.25em] text-white/50 font-semibold">Estimated monthly repayment</div>
          <div className="font-display text-4xl sm:text-5xl font-bold text-gold mt-3" data-testid="repayment-monthly">{fmtAUD(monthly)}</div>
          <div className="grid grid-cols-2 gap-6 mt-8 pt-8 border-t border-white/10">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/45">Total interest</div>
              <div className="font-display text-lg font-bold mt-1" data-testid="repayment-total-interest">{fmtAUD(totalInterest)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/45">Total repayable</div>
              <div className="font-display text-lg font-bold mt-1">{fmtAUD(amount + totalInterest)}</div>
            </div>
          </div>
        </div>
        <div className="mt-8 h-56" data-testid="repayment-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <defs>
                <linearGradient id="bal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FDC830" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#FDC830" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={false} interval={Math.ceil(years / 6)} />
              <YAxis hide domain={[0, amount]} />
              <Tooltip formatter={(v) => [fmtAUD(v), "Balance"]} contentStyle={{ borderRadius: 14, border: "1px solid rgba(15,23,42,0.08)", fontSize: 13 }} />
              <Area type="monotone" dataKey="balance" stroke="#F5B70A" strokeWidth={2.5} fill="url(#bal)" />
            </AreaChart>
          </ResponsiveContainer>
          <p className="text-center text-[11px] text-ink/40 mt-1">Remaining balance over time</p>
        </div>
      </div>
    </div>
  );
}

function BorrowingCalc() {
  const [income, setIncome] = useState(180000);
  const [expenses, setExpenses] = useState(3500);
  const [debts, setDebts] = useState(500);
  const [deposit, setDeposit] = useState(150000);

  const { estimate, price } = useMemo(() => {
    const surplus = Math.max(0, income / 12 - expenses - debts);
    const capacity = surplus * 0.8;
    const ar = (6.19 + 3) / 100 / 12;
    const n = 360;
    const borrow = capacity > 0 ? (capacity * (1 - Math.pow(1 + ar, -n))) / ar : 0;
    return { estimate: borrow, price: borrow + deposit };
  }, [income, expenses, debts, deposit]);

  const fields = [
    { label: "Gross annual income", value: income, set: setIncome, min: 40000, max: 800000, step: 5000, testid: "calculator-income-input", display: fmtAUD(income) },
    { label: "Monthly living expenses", value: expenses, set: setExpenses, min: 1000, max: 15000, step: 100, testid: "calculator-expenses-input", display: `${fmtAUD(expenses)}/mo` },
    { label: "Existing monthly repayments", value: debts, set: setDebts, min: 0, max: 10000, step: 100, testid: "calculator-debts-input", display: `${fmtAUD(debts)}/mo` },
    { label: "Deposit available", value: deposit, set: setDeposit, min: 20000, max: 2000000, step: 10000, testid: "calculator-deposit-input", display: fmtAUD(deposit) },
  ];

  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
      <div className="space-y-10">
        {fields.map((f) => (
          <div key={f.testid}>
            <div className="flex justify-between items-baseline mb-4">
              <Label className="text-sm font-semibold text-ink">{f.label}</Label>
              <span className="font-display text-xl font-bold text-ink">{f.display}</span>
            </div>
            <Slider data-testid={f.testid} value={[f.value]} onValueChange={([v]) => f.set(v)} min={f.min} max={f.max} step={f.step} />
          </div>
        ))}
      </div>
      <div>
        <div className="rounded-3xl bg-obsidian text-white p-8 sm:p-10 grain relative overflow-hidden" data-testid="borrowing-result">
          <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-brand/25 blur-3xl pointer-events-none" />
          <div className="text-xs uppercase tracking-[0.25em] text-white/50 font-semibold">Indicative borrowing power</div>
          <div className="font-display text-4xl sm:text-5xl font-bold text-gold mt-3" data-testid="borrowing-estimate">{fmtAUD(estimate)}</div>
          <div className="grid grid-cols-2 gap-6 mt-8 pt-8 border-t border-white/10">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/45">Potential purchase price</div>
              <div className="font-display text-lg font-bold mt-1" data-testid="borrowing-price">{fmtAUD(price)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/45">Assessment basis</div>
              <div className="font-display text-lg font-bold mt-1">9.19% buffered</div>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-ink/40 leading-relaxed mt-6">
          Indicative only. Calculated at a buffered assessment rate with a 20% shading on surplus income over a 30-year
          term. Actual borrowing capacity varies significantly by lender, income type, dependants, credit history and
          existing commitments — a precise assessment is part of your first meeting.
        </p>
      </div>
    </div>
  );
}

export default function Calculators() {
  return (
    <div data-testid="calculators-page">
      <section className="pt-36 sm:pt-44 pb-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Plan with precision</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink leading-[1.05] max-w-3xl">
              Run the numbers. <span className="font-editorial italic font-medium text-gold-600">Then run them past us.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal delay={0.1}>
            <Tabs defaultValue="repayment">
              <TabsList data-testid="calculator-tabs" className="rounded-full p-1.5 bg-ink/5 mb-12">
                <TabsTrigger data-testid="tab-repayment" value="repayment" className="rounded-full px-6 py-2.5 text-sm font-semibold">Repayment</TabsTrigger>
                <TabsTrigger data-testid="tab-borrowing" value="borrowing" className="rounded-full px-6 py-2.5 text-sm font-semibold">Borrowing power</TabsTrigger>
              </TabsList>
              <TabsContent value="repayment"><RepaymentCalc /></TabsContent>
              <TabsContent value="borrowing"><BorrowingCalc /></TabsContent>
            </Tabs>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
